import React, { useCallback, useState } from 'react'
import {
  MetabaseProvider,
  StaticQuestion,
} from '@metabase/embedding-sdk-react'
import apis from 'browser/app/models/apis'
import { IBaseProps } from '../../atoms/base-props'
import classNames from 'classnames'
import _ from 'lodash'
import './metabase-kpis.scss'

/**
 * @uiComponent
 *
 * Displays multiple Metabase questions (number/KPI type) in a responsive grid layout.
 * All questions share a single MetabaseProvider for efficient auth token reuse.
 *
 * Usage:
 * {
 *   "type": "ui:metabaseKpis",
 *   "title": "Audit Accuracy",
 *   "columns": 3,
 *   "resourceIds": [
 *     { "resourceId": "542", "params": { "execution_id": "..." } },
 *     { "resourceId": "3968", "params": { "execution_id": "..." } }
 *   ]
 * }
 */

interface IMetabaseKpiResource {
  /** The Metabase question ID */
  resourceId: string
  /** Label displayed below the value */
  title?: string
  /** Context parameters to pass to this question */
  params?: Record<string, any>
}

export interface IMetabaseKpisProps extends IBaseProps {
  /** Title displayed above the KPI grid */
  title?: string
  /** List of Metabase question resources to display */
  resourceIds?: IMetabaseKpiResource[]
  /** Number of columns in the grid (default 3) */
  columns?: number
  /** Whether to show border around the container */
  showBorder?: boolean
  /** Whether to show border around each KPI cell */
  showCellBorder?: boolean
  /** Custom height for each KPI cell */
  height?: string | number
}

export const MetabaseKpis: React.FC<IMetabaseKpisProps> = ({
  title,
  resourceIds = [],
  columns = 3,
  showBorder = false,
  showCellBorder = false,
  height = '60px',
  className = '',
}) => {
  const [error, setError] = useState<string | undefined>()
  const metabaseUrl = 'https://stats.withvector.com'

  const fetchRequestToken = useCallback(async () => {
    try {
      const response = await apis.getMetabaseEmbedToken()
      if (!response?.token) {
        throw new Error('Failed to get JWT token from backend')
      }
      return { id: response.token, jwt: response.token }
    } catch (err) {
      const message = err.message || 'Failed to authenticate with Metabase'
      console.error('[MetabaseKpis] SDK error fetching JWT token:', err)
      setError(message)
      throw err
    }
  }, [])

  const containerClassName = classNames(
    'metabase-kpis',
    className,
    { 'metabase-kpis--bordered': showBorder },
  )

  if (_.isEmpty(resourceIds)) {
    return (
      <div className={containerClassName}>
        {!_.isEmpty(title) && <div className="metabase-kpis__title">{title}</div>}
        <div className="metabase-kpis__error">
          <p>No resources configured</p>
        </div>
      </div>
    )
  }

  return (
    <div className={containerClassName}>
      {!_.isEmpty(title) && <div className="metabase-kpis__title">{title}</div>}

      {error && (
        <div className="metabase-kpis__error">
          <p>Unable to load analytics: {error}</p>
        </div>
      )}

      {!error && (
        <MetabaseProvider
          authConfig={{
            metabaseInstanceUrl: metabaseUrl,
            preferredAuthMethod: 'jwt',
            fetchRequestToken,
          }}
        >
          <div
            className="metabase-kpis__grid"
            style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
          >
            {resourceIds.map((resource) => (
              <div key={resource.resourceId} className={classNames('metabase-kpis__cell', { 'metabase-kpis__cell--bordered': showCellBorder })}>
                <div className="metabase-kpis__value">
                  <StaticQuestion
                    title={false}
                    questionId={Number(resource.resourceId)}
                    initialSqlParameters={resource.params || {}}
                    height={height}
                  />
                </div>
                {resource.title && (
                  <label className="c-label c-label--secondary">{resource.title}</label>
                )}
              </div>
            ))}
          </div>
        </MetabaseProvider>
      )}
    </div>
  )
}
